/**
 * Migrate from Shopify (MERCH-90), client side. Pure. The client reads Shopify's product CSV export into one object
 * per line keyed by its header and sends them as they are; the server (lib/catalog/shopifyImport, InventoryUnit
 * shopifyPreview / importShopify) groups them into products, checks the merchant's mapping, answers the dry-run report
 * and imports all or nothing. Problems name the file's line (path `rows.<line>.<column>`; variantCsv rowProblems).
 */

// The columns that make a file a Shopify product export: the server's lib/catalog/shopifyFile REQUIRED_HEADERS
// (a parity test pins them together).
export const REQUIRED_HEADERS = ['Handle', 'Title', 'Variant Price'];
// A source type the merchant chose not to import (the Select needs a value; the server gets null).
export const SKIP = '__skip__';

const headerOf = (table) => ((table && table[0]) || []).map((h) => String(h).trim());

/** Does this parsed table look like a Shopify product export? */
export function isShopifyExport(table) {
  const header = headerOf(table).map((h) => h.toLowerCase());
  return REQUIRED_HEADERS.every((c) => header.includes(c.toLowerCase()));
}

/** A parsed CSV table (lib/csv parseCsv) → one object per line, keyed by the header as written. */
export function shopifyRecords(table) {
  const header = headerOf(table);
  return (table || []).slice(1)
    .filter((r) => r.some((c) => String(c).trim() !== ''))
    .map((r) => Object.fromEntries(header.map((h, i) => [h, String(r[i] ?? '')])));
}

/** The mapping the form starts from: the report's own (the server's suggestions on a first check). */
export function initialMapping(report) {
  const m = (report && report.mapping) || {};
  return {
    types: Object.fromEntries(((report && report.source_types) || []).map((s) => [s.source, s.mapped || SKIP])),
    condition: m.condition || '',
    grade_column: m.grade_column || '',
    fields: m.fields || {},
    attributes: m.attributes || {},
    specs: m.specs || {},
    images: m.images !== false,
  };
}

/** The type keys the form maps to (distinct, in the order of the file's types). */
export const mappedKeys = (form) => [...new Set(Object.values(form.types || {}).filter((k) => k && k !== SKIP))];

/** The form → the mapping the server takes: skipped types as null, specs and fields only for mapped types. */
export function mappingForServer(form) {
  const keys = mappedKeys(form);
  const only = (obj) => Object.fromEntries(Object.entries(obj || {}).filter(([k, v]) => keys.includes(k) && v && Object.keys(v).length));
  const fields = Object.fromEntries(Object.entries(only(form.fields)).map(([k, cols]) => [k, Object.fromEntries(Object.entries(cols).filter(([, f]) => f))]));
  const attributes = Object.fromEntries(Object.entries(only(form.attributes)).map(([k, a]) => [k, Object.fromEntries(Object.entries(a).filter(([, v]) => v !== '' && v != null))]));
  const specs = Object.fromEntries(Object.entries(only(form.specs)).map(([k, cols]) => [k, Object.fromEntries(Object.entries(cols).filter(([, a]) => a))]));
  return {
    types: Object.fromEntries(Object.entries(form.types || {}).map(([s, k]) => [s, k && k !== SKIP ? k : null])),
    ...(form.condition ? { condition: form.condition } : {}),
    ...(form.grade_column ? { grade_column: form.grade_column } : {}),
    fields: Object.fromEntries(Object.entries(fields).filter(([, cols]) => Object.keys(cols).length)),
    attributes: Object.fromEntries(Object.entries(attributes).filter(([, a]) => Object.keys(a).length)),
    specs: Object.fromEntries(Object.entries(specs).filter(([, cols]) => Object.keys(cols).length)),
    images: form.images !== false,
  };
}

/** The conditions every mapped type allows (the batch's one condition comes from these), with their grade rule. */
export function sharedConditions(report, form) {
  const types = ((report && report.types) || []).filter((t) => mappedKeys(form).includes(t.key));
  if (!types.length) return [];
  return types[0].conditions
    .filter((c) => types.every((t) => t.conditions.some((x) => x.key === c.key)))
    .map((c) => ({ ...c, requires_grade: types.some((t) => (t.conditions.find((x) => x.key === c.key) || {}).requires_grade) }));
}

/** Does the chosen condition need a grade column for some mapped type? */
export function needsGrade(report, form) {
  return sharedConditions(report, form).some((c) => c.key === form.condition && c.requires_grade);
}
